(ns app.domain.workspace.model
  (:require #?(:clj [core.db.entity :as entity])
            #?(:clj [datomic.api :as d])
            [app.calendar :as calendar]
            [app.domain.attachment.model :as attachment-model]
            [app.domain.group.model :as group-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.municipality.model :as muni-model]
            [app.domain.property.id-util :as property-id-util]
            [app.domain.property.model :as property-model]
            [app.domain.util :as du]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.db.child-entity :as child]
            [core.db.entity.util :as eu]
            [core.util.util :as util]
            [schema-tools.core :as st]
            [schema.core :as s])
  (:import #?(:clj [clojure.lang PersistentVector])))

(def distinct-seq (comp seq distinct))

(def group-number-pattern
  "[A-Z0-9]+")

(def workspace-id-pattern
  (str "\\w{2}-" group-number-pattern "-\\w{1,3}(-TEMP)?-\\d{4}-\\d+"))


(s/defschema GroupNumber
  (s/constrained s/Str #(re-matches (re-pattern group-number-pattern) %)))


(def check-group-number (s/checker GroupNumber))


(defn group-number? [v] (nil? (check-group-number v)))


(s/defschema WorkspaceId
  (s/constrained s/Str #(re-matches (re-pattern workspace-id-pattern) %)))


(def check-workspace-id (s/checker WorkspaceId))


(defn workspace-id? [v] (nil? (check-workspace-id v)))


(def workspace-states
  [:workspace.state/pre-consultation
   :workspace.state/application
   :workspace.state/execution
   :workspace.state/archived
   :workspace.state/periodic-parent])

(def phase-types-in-order
  [:entity.type/pre-consultation
   :entity.type/application
   :entity.type/periodic-parent
   :entity.type/execution
   :entity.type/archived])

(s/defschema WorkspaceState (apply s/enum workspace-states))

(def workspace-application
  (child/child-entity-of-type-fn :entity.type/application))

(def workspace-pre-consultation
  "Returns the pre-consultation entity for the given workspace."
  (child/child-entity-of-type-fn :entity.type/pre-consultation))

(defn own-or-prior-pre-consultation
  "Return either the pre-consultation from the given workspace, or the prior pre-consultation from another workspace
   that is considered to be also the pre-consultation for this workspace."
  [workspace]
  (or (workspace-pre-consultation workspace)
      (:workspace/prior-pre-consultation workspace)))

(def workspace-archiving
  "Returns the archiving entity for the given workspace."
  (child/child-entity-of-type-fn :entity.type/archiving))

(def workspace-execution
  "Returns the execution entity for the given workspace."
  (child/child-entity-of-type-fn :entity.type/execution))

(def workspace-periodic-parent
  (child/child-entity-of-type-fn :entity.type/periodic-parent))

(def workspace-state->child-type
  {:workspace.state/application      :entity.type/application
   :workspace.state/execution        :entity.type/execution
   :workspace.state/pre-consultation :entity.type/pre-consultation
   :workspace.state/archived         :entity.type/archiving
   :workspace.state/periodic-parent  :entity.type/periodic-parent})

(def phase-entity-types
  (vals workspace-state->child-type))

(s/defschema PhaseEntityType (apply s/enum phase-entity-types))

(def is-phase-entity?
  "Predicate to check if an entity is a 'phase-entity', e.g. application, execution or similar"
  (apply eu/is-type-fn phase-entity-types))

#_{:clj-kondo/ignore [:def-fn]}
(def ^{:arglists '([workspace])} phase-entities
  "Get the pre-consultation, application, execution etc. entities for a given workspace.
  Consider using `phase-entities-with-prior-pre-con` if your context will access pre-consultation data and
  the original pre-consultation under another workspace should be taken into account."
  (let [fallback #(->> % :entity/_parent (filter is-phase-entity?))]
    #?(:cljs fallback
       :clj  (fn [workspace]
               (if (entity/entity? workspace)
                 (d/q '[:find [?phase-entity ...]
                        :in $ [?phase-tuples ...]
                        :where
                        [?phase :entity/parent+type ?phase-tuples]
                        [(datomic.api/entity $ ?phase) ?phase-entity]]
                      (d/entity-db workspace)
                      (->> phase-entity-types
                           (map (fn [t] [(:db/id workspace) t]))))
                 (fallback workspace))))))

(defn phase-entities-with-prior-pre-con
  "Get the pre-consultation, application, execution etc. entities for a given workspace,
  including the pre-consultation linked via :workspace/prior-pre-consultation, if any."
  [{:workspace/keys [prior-pre-consultation] :as workspace}]
  (cond->> (phase-entities workspace)
    prior-pre-consultation (cons prior-pre-consultation)))

(defn phase-type-sort-order [item]
  (if (some #{(:entity/type item)} phase-types-in-order)
    (.indexOf ^PersistentVector phase-types-in-order (:entity/type item))
    (count phase-types-in-order)))

(defn previous-phase-entity-types
  "Get previous phase-entity types for a given workspace. If none, returns ()"
  [workspace current-phase-entity-type]
  (let [workspace-phases-sorted (->> (phase-entities workspace)
                                     (sort-by phase-type-sort-order)
                                     (mapv :entity/type))]
    (take-while #(not= current-phase-entity-type %) workspace-phases-sorted)))

(defn workspace-target
  "Give a workspace, output the currently applicable child entity"
  [workspace]
  ((child/child-entity-of-type-fn (-> workspace
                                      :domain/state
                                      (workspace-state->child-type :entity.type/application)))
   workspace))

(defn project [workspace]
  (:entity/parent workspace))

(def ^{:arglists '([workspace])} packages
  (child/child-entities-of-type-fn :entity.type/package-pdf))

(defn is-workspace? [entity]
  (-> entity :entity/type (= :entity.type/workspace)))


(defn is-secondary-property-fn [workspace]
  (fn [{:db/keys [id]}]
    (not= (-> workspace :workspace/primary-property :db/id)
          id)))

(defn property-sort-fn [workspace]
  (juxt (is-secondary-property-fn workspace)
        :property/id))

(defn property-data-list
  "Get value of some property attribute from all properties of the `workspace`.
   Value from the primary property will be the head of the returned list."
  [workspace property-attribute]
  (->> workspace
       :workspace/properties
       (sort-by (property-sort-fn workspace))
       (keep property-attribute)))

(defn property-data [workspace property-data-key]
  (->> (property-data-list workspace property-data-key)
       (str/join " / ")))


(defn single-property [workspace]
  (:workspace/primary-property workspace))


(defn municipality-name [lang workspace]
  (let [municipality (-> workspace :workspace/municipality)]
    (muni-model/municipality-name municipality lang)))


(defn municipality-names
  "Workspace municipality name concatenated across `langs`,
   for multi-language PDF rendering."
  [workspace langs]
  (-> workspace
      :workspace/municipality
      :municipality/names
      (i18n-model/concat-localized-strings langs)))


(defn customer-id
  "Get the GIS customer id / domain group id for this workspace, e.g. CA-1234567"
  [workspace]
  (-> workspace :domain/authority-group :entity/parent :group/id))


(defn full-address
  "Format a full address string from address components."
  ([workspace]
   (-> workspace :workspace/structural-address :address/full-address)))


(defn full-address-with-units
  "Format a full address string from address components."
  [workspace lang tr]
  (property-model/format-address-with-units
    (full-address workspace)
    (->> workspace :workspace/property-units)
    #(property-model/unit-designator-name % lang)
    (tr [:address/multi-unit])))


(defn property-units
  "Active property units for the workspace's primary property."
  [workspace]
  (property-model/active-property-units (:workspace/primary-property workspace)))


(defn street-name [workspace]
  (-> workspace :workspace/structural-address :address/street-name))


(defn address-number
  "Street number with optional suffix (e.g. \"123\", \"123A\")."
  [workspace]
  (let [{:address/keys [street-number street-number-suffix]}
        (:workspace/structural-address workspace)]
    (when street-number
      (str street-number street-number-suffix))))


(defn address-with-units
  "Append unit info to the workspace address.
   Caller supplies `designator-name-fn` (for the 1-unit display-key) and
   a localized `multi-unit-label` (used for 2+ units)."
  [workspace designator-name-fn multi-unit-label]
  (property-model/format-address-with-units (:workspace/address workspace)
                                            (:workspace/property-units workspace)
                                            designator-name-fn
                                            multi-unit-label))

(defn- format-units
  "Comma-joined extracted values from workspace's referenced property units.
   Returns \"\" when there are no units."
  [workspace extract-fn]
  (->> workspace
       :workspace/property-units
       (map extract-fn)
       (remove str/blank?)
       distinct
       (str/join ", ")))


(defn unit-type
  "Distinct designator name(s) of the workspace's referenced property units."
  [workspace designator-name-fn]
  (format-units workspace (comp designator-name-fn :property-unit/designator)))


(defn unit-number
  "Identifier(s) of the workspace's referenced property units."
  [workspace]
  (format-units workspace :property-unit/identifier))


(defn unit-type-and-number
  "Localized display-key(s) of the workspace's referenced property units."
  [workspace designator-name-fn]
  (format-units workspace #(property-model/localized-unit-display-key % designator-name-fn)))


(def dashboard-filter-condition-attributes
  [:workspace/permit-type
   :main-permit-type
   :workspace/type
   :workspace/assignee
   :domain/state
   :workspace/category
   :application/work-type-names
   :application/work-target-names
   :review/assignee
   :approval/assignee
   :approval/user-group
   :completion-date
   :group/id
   :municipality/number])


(def workspace-types
  [:workspace-type/application ; Not for CE, PWO
   :workspace-type/case ; Only for CE
   :workspace-type/work-order; Only for PWO
   :workspace-type/building-supplemental ; :permit-type/BS
   :workspace-type/licence-follow-up ; :permit-type/LS
   ; permit-type/PI + :workspace.state/periodic-parent
   :workspace-type/periodic-inspection-parent
   ; permit-type/PI + :workspace.state/execution
   :workspace-type/periodic-inspection-child])


(def retriggering-filter-condition-attributes
  "These `dashboard-filter-condition-attributes` will trigger refetching
  of filter values from the server when changed."
  #{:main-permit-type
    :workspace/category
    :application/work-target-names
    :application/work-type-names
    :workspace/type
    :group/id})


(def all-filter-condition-attributes
  (concat dashboard-filter-condition-attributes
          [:project-id
           :property/id]))


(def common-search-sort-attributes
  [:submitted
   :created
   :modified
   :application/target-time-date])


(def all-search-sort-attributes
  (concat common-search-sort-attributes
          [:approval-due-date
           :review-due-date
           :pre-con-expiration-date]))

(defn default-search-sort [is-authority?]
  (if is-authority?
    [:submitted :desc]
    [:created :desc]))

(s/defschema WorkspaceSearchSorting
  (s/pair (apply s/enum all-search-sort-attributes) "Sort attribute"
          (s/enum :asc :desc) "Sort direction"))


(defn sanitize-pt-path
  [permit-type category work-type work-target]
  (->> [permit-type
        category
        work-type
        work-target]
       (take-while some?)
       vec))

(defn fix-cardinality [permit-type work-types]
  (cond
    ;; permit-type may have multiple work-types
    (contains? ptc-model/permit-types-with-multiple-work-types permit-type)
    (set work-types)

    ;; only one work-type allowed, but multiple found. database is bad
    (next work-types)
    (throw (ex-info (str "Multiple work-types in " permit-type " permit")
                    (util/mmap permit-type work-types)))

    ;; flatten to one item in tuple if no multiple work-types possible
    :else
    (first work-types)))

(defn default-config-path
  "Returns the config path for the workspace with application work-type and work-target if
   the workspace includes an application, or for the workspace.

  The third element (work-types) may contain one or multiple items depending on the permit-type,
  e.g. [pt cat wt wta], [pt cat] and [pt cat #{wt1 wt2}] are all valid tuples."
  [{:workspace/keys [permit-type permit-type-group-config
                     category-group-config work-type-group-configs work-target-group-config] :as workspace}]
  (let [app                      (workspace-application workspace)
        work-type-group-configs  (or (:application/work-type-group-configs app) work-type-group-configs)
        work-target-group-config (or (:application/work-target-group-config app) work-target-group-config)]
    (sanitize-pt-path permit-type-group-config
                      category-group-config
                      (fix-cardinality permit-type work-type-group-configs)
                      work-target-group-config)))

(defn flatten-config-path
  "Given a config tuple obtained with `default-config-path`, return a flat collection with the configs"
  [config-tuple]
  (->> config-tuple
       (mapcat (fn [config-or-set]
                 (if (set? config-or-set)
                   config-or-set
                   [config-or-set])))))

(defn leaf-config
  "Returns the leaf (lowest tier) group config assigned to the workspace + application.
   If the leaf tier is a set, returns the set.
   This is useful, because by going through the config parents the full workspace config path can be recovered."
  [workspace]
  (last (default-config-path workspace)))

(defn leaf->config-path
  "Reconstruct a full workspace config path comparable to `default-config-path` from the leaf config
   (or multiple leaf configs if multiple work-types are supported)"
  [permit-type leaf-configs]
  (let [single-config (if (set? leaf-configs)
                        (first leaf-configs)
                        leaf-configs)]
    (concat (some->> single-config :entity/parent (leaf->config-path permit-type))
            [(if (and (set? leaf-configs)
                      (contains? ptc-model/permit-types-with-multiple-work-types permit-type)
                      (-> single-config :group.config/level (= :group-config.level/work-type)))
               leaf-configs
               single-config)])))

(defn max-neighbour-radius [sou]
  (get {:imperial 1500                  ; ft
        :metric   500}                  ; m
       sou
       200))


(defn default-neighbour-radius [sou]
  (get {:imperial 100                      ; ft
        :metric   30}                      ; m
       sou
       30))


(defn valid-neighbour-radius? [max-radius radius]
  (<= 0 radius max-radius))


(def workspace-sensitivity-values
  [:workspace.sensitivity-level/low
   :workspace.sensitivity-level/medium
   :workspace.sensitivity-level/high])


(def valid-sensitivity-value?
  (partial contains? (set workspace-sensitivity-values)))


(def default-value-for-workspace-sensitivity
  :workspace.sensitivity-level/low)

(defn neighbour-id
  "The id a neighbour is known by: its property id when looked up by property id,
   otherwise its integration-id."
  [property property-id-lookup?]
  (if property-id-lookup?
    (:property/id property)
    (:property/integration-id property)))

(defn neighbour-ids
  ([workspace] (neighbour-ids workspace false))
  ([workspace property-id-lookup?]
   (->> workspace
        :workspace/neighbour-properties
        (map #(neighbour-id % property-id-lookup?)))))


(defn neighbour-map-ids
  "ID for each stored neighbour, mapped to every variant the map may use
   for it.

   The map component matches parcels by string, so a neighbour whose stored id
   was normalized (an Ontario roll padded to 19 digits) has to be offered under
   both variants or the layer will not recognise it. The key is the id we
   store; the value is every variant to seed."
  [workspace property-id-lookup? subdivision-or-country]
  (into {}
        (keep (fn [property]
                (when-let [id (neighbour-id property property-id-lookup?)]
                  [id (if property-id-lookup?
                        (property-id-util/denormalized-property-ids subdivision-or-country id)
                        [id])])))
        (:workspace/neighbour-properties workspace)))


(defn neighbour-map-seed-ids
  "Every variant to hand the map, flattened from `neighbour-map-ids`."
  [map-ids]
  (into [] cat (vals map-ids)))


(defn neighbours-after-map-change
  "The neighbours to store, given what the map sent back.

   Translates the map's answer back into our own ids. The map drops only the
   variant it matched, so a neighbour counts as still selected only while
   every variant we seeded for it comes back; anything returned that we never
   seeded is a parcel the user just picked, and is kept as the map returned it."
  [map-ids returned]
  (let [returned (into #{} (keep identity) returned)
        seeded   (into #{} cat (vals map-ids))]
    (into (into #{} (remove seeded) returned)
          (keep (fn [[id variants]] (when (every? returned variants) id)))
          map-ids)))

(defmethod group-model/get-authority-group :entity.type/workspace
  [workspace]
  (-> workspace :domain/authority-group))

(defn workspace-tr-fn
  "Make a normal tr function into one that does context-specific
  changes to the keys used

  Do not use this function from frontend code if you have the workspace
  context loaded - use `::ws-subs/tr` instead, as that one doesn't cause
  a translation reload every time the entity-db changes."
  [tr {:keys [:workspace/permit-type
              :domain/authority-group]}]
  (i18n-model/wrap-tr-fn
    (merge (group-model/get-i18n-mapping authority-group)
           (-> permit-type i18n-model/permit-type-specific-keys))
    tr))

(defn get-tz
  "Get the timezone name associated with this workspace (via authority group)"
  [workspace]
  (-> workspace group-model/get-authority-group :group/tz))


(s/defschema ConfigDataForWsView
  (st/merge
    {:review-configs         [s/Int]
     :group-features         #{s/Keyword}
     :instr-text-for-phases  (s/maybe {s/Keyword s/Str})
     :attachment-types       #{s/Int}
     :available-supplemental #{ptc-model/PermitTypeAndCategory}
     :hidden?                s/Bool}
    (st/optional-keys
      {:payment-card-types    [s/Str]
       :hidden-description    s/Str
       :hidden-title-tr-key   s/Keyword
       :hidden-help-tr-key    s/Keyword
       ;; included after sign-off, otherwise look up from configs
       :sign-off-declarations [(s/pred eu/has-some-type?)]
       ;; use role keys instead of entity ids for guards and time-travel
       ;; only included if already signed off, otherwise, look up from configs
       :required-so-roles     [s/Keyword]})))


(s/defschema WsGetResponse
  (st/merge ConfigDataForWsView
            {:entities                        {s/Int (s/pred eu/has-some-type?)}
             :conversation-notification-count s/Int}
            (st/optional-keys
              {:last-state-change-date                   (s/maybe s/Inst)
               :payment-use-municipal-case-id            s/Bool
               :limited-visibility-for-other-departments s/Bool
               :process-flow                             s/Keyword
               :unfinished-import-session-id             s/Int})))


(def config-data-for-ws-view-keys (keys (st/required-keys ConfigDataForWsView)))


(def priorities [:workspace.priority/high :workspace.priority/normal :workspace.priority/low])


(def default-priority :workspace.priority/normal)

(defn has-location [workspace]
  (or (-> workspace :workspace/primary-property :entity/location some?)
      (-> workspace :entity/location some?)))

(s/defschema CasePriority (apply s/enum priorities))

(defn active-reviewers
  "Active reviewer is assigned in a review in that workspace that is not Reviewed"
  [workspace]
  (let [application (workspace-application workspace)]
    (->> application
         ((child/child-entities-of-type-fn :entity.type/review))
         (filter #(not= (:review/status %) :review.status/approved))
         (keep :review/assignee)
         distinct-seq)))

(defn inspections [workspace]
  (->> [(workspace-execution workspace)
        (workspace-application workspace)]
       (remove nil?)
       (mapcat (child/child-entities-of-type-fn :entity.type/inspection))))

(defn active-inspectors
  "Active inspector is assigned in an inspection in that workspace that is not Completed"
  [workspace]
  (let [compl-inspections (->> workspace
                               inspections
                               (filter (complement (du/in-state-fn :inspection.state/completed))))
        booked-inspectors (->> compl-inspections
                               (mapcat :entity/_parent)
                               (filter (every-pred (eu/is-type-fn :entity.type/booking)
                                                   (du/in-state-fn :booking.state/dispatched)))
                               (keep :booking/assignee))]
    (->> compl-inspections
         (mapcat :entity/_parent)
         (filter (eu/is-type-fn :entity.type/inspection-visit))
         (mapcat :inspection.visit/inspectors)
         (concat booked-inspectors)
         distinct-seq)))

(defn active-authority-users
  "Calculates active authority users for workspace.


  Active authority user is
    Workspace assignee OR
    Assigned in a review in that workspace that is not Reviewed OR
    Assigned in a inspection in that workspace that is not Completed"
  [workspace]
  (distinct (concat
              (some-> (:workspace/assignee workspace) vector)
              (active-reviewers workspace)
              (active-inspectors workspace))))

(def restricted-es-attrs
  "Attributes to strip from workspace search results if user has only limited read permissions"
  #{:workspace/category-names
    :workspace/case-description
    :property-owners
    :applicants
    :bills})

(def ^{:arglists '([workspace])} case-id
  "Either the municipal case ID, if assigned, or workspace ID, to use in places where municipal case ID is preferred."
  (some-fn :workspace/municipal-case-id
           :workspace/id))

(defn get-related-targets [target]
  (let [{my-permit-type :workspace/permit-type
         :as            workspace}             (:entity/parent target)]
    (->> workspace
         :workspace/related-workspaces
         (filter (comp (partial ptc-model/permit-type-matches-as-primary-for my-permit-type)
                       :workspace/permit-type))
         (mapcat phase-entities)
         (filter (apply du/in-state-fn attachment-model/show-attachments-from-related-phases-states)))))

(defn attachments-from-related [{my-permit-type :workspace/permit-type :as workspace}]
  (->> workspace
       :workspace/related-workspaces
       (filter (comp (partial ptc-model/permit-type-matches-as-primary-for my-permit-type)
                     :workspace/permit-type))
       (mapcat phase-entities)
       (filter (apply du/in-state-fn attachment-model/show-attachments-from-related-phases-states))
       (mapcat attachment-model/phase-attachments)))

(defn sibling-workspaces [{:workspace/keys [parent-workspace permit-type] :as workspace}]
  (when parent-workspace
    (->> parent-workspace
         :workspace/_parent-workspace
         (remove #(= (:db/id %) (:db/id workspace)))
         (remove #(and (= permit-type :permit-type/PI)
                       (calendar/after (:entity/created %) (:entity/created workspace)))))))
